<template>
    <div
        class="ai-card flex flex-col gap-4 rounded border border-solid border-grey-40 px-6 py-4"
    >
        <div class="flex items-center gap-2">
            <PtIcon icon="VectorGradient" class="h-8 w-8"></PtIcon>
            <span class="text-xl font-semibold"> {{ t('whatsNew.title') }} </span>
            <PtIcon icon="AiStar" class="h-7 w-7"></PtIcon>
        </div>

        <div class="rounded bg-white px-4 py-3 text-black" v-if="searchText">
            <div class="relative mb-3 flex justify-between text-sm">
                <span class="flex items-center font-semibold">
                    <div
                        class="absolute -left-4 top-0 h-5 w-1 rounded-l-none rounded-r-md bg-primary"
                    />
                    {{ t('whatsNew.forYourSearch') }}
                    <span class="ml-2 text-primary">
                        {{ t('leftQuote') }}{{ searchText }}{{ t('rightQuote') }}
                    </span>

                    <div class="th-tooltip ml-2 text-left font-normal" :data-tip="t('whatsNew.forYourSearchTooltip')">
                        <PtIcon icon="SolidInfo" class="fill-grey-55 text-2xl" />
                    </div>
                </span>

                <span
                    class="cursor-pointer select-none text-primary"
                    @click="() => handleViewMore({
                        solution_ids: queryData[0]?.patent_ids
                    })"
                    v-if="!isQueryLoading && queryData.length && queryData[0]?.patent_ids?.length"
                >
                    {{ t('whatsNew.viewMore') }}
                </span>
            </div>

            <div v-if="isQueryLoading" class="flex justify-center py-8">
                <span class="th-loading th-loading-spinner th-loading-lg block bg-grey-200"></span>
            </div>

            <div
                v-else-if="!queryData.length"
                class="flex flex-col items-center justify-center py-8"
            >
                <CatHole2 class="mb-2 h-[50px] w-[70px]"/>
                <span class="text-sm text-grey-90">
                    {{ t('whatsNew.no90Data') }}
                </span>
            </div>

            <ul
                class="list-disc text-sm text-grey-450"
                v-else
            >
                <li class="mb-1 ml-4" v-for="(item, idx) in queryData" :key="idx">
                    {{ item.content }}
                </li>
            </ul>
        </div>

        <div class="flex flex-col gap-4 rounded bg-white px-4 py-3">
            <div class="relative flex items-center text-sm font-semibold">
                <div
                    class="absolute -left-4 top-0 h-5 w-1 rounded-l-none rounded-r-md bg-primary"
                ></div>
                {{ t('whatsNew.last90Days') }}

                <div class="th-tooltip ml-2 text-left font-normal" :data-tip="t('whatsNew.last90DaysTooltip')">
                    <PtIcon icon="SolidInfo" class="fill-grey-55 text-2xl" />
                </div>
            </div>

            <div v-if="isLastDayLoading" class="mx-auto py-8">
                <span class="th-loading th-loading-spinner th-loading-lg block bg-grey-200"></span>
            </div>

            <div
                v-else-if="!last90DayData.length"
                class="flex flex-col items-center justify-center py-8"
            >
                <CatHole2 class="mb-2 h-[50px] w-[70px]"/>
                <span class="text-sm text-grey-90">
                    {{ t('noData') }}
                </span>
            </div>

            <template v-else>
                <div
                    class="border-b border-b-grey-40 pb-4 text-grey-450 last:border-b-0 last:pb-0"
                    v-for="(item, idx) in last90DayData"
                    :key="idx"
                >
                    <p class="mb-2 flex justify-between text-sm font-medium">
                        <span class="font-bold">{{ item.title }}</span>
                        <span v-if="item.patent_ids?.length" @click="() => handleViewMore({
                            solution_ids: item.patent_ids,
                            footerField: 'PBD'
                        })" class="cursor-pointer select-none text-primary">{{
                            t('whatsNew.viewMore')
                        }}</span>
                    </p>

                    <ul class="list-disc text-sm">
                        <li class="mb-1 ml-4">{{ item.content }}</li>
                    </ul>
                </div>
            </template>

        </div>
    </div>
</template>

<script setup lang="ts">
import '@patsnap-ui/icon/assets/VectorGradient.svg';
import '@/assets/icon/aiStar.svg';
import { PtIcon } from 'patsnap-biz';
import { useWhatsNew } from '@/composables/use-whats-new';
import { useI18n } from 'vue-i18n';
import { CatHole2 } from '@eureka/cat';
import { useRoute } from 'vue-router';

const route = useRoute();

const query = route.query.q
const searchText = query ? (query as string).replace(/IN:|TTC:|ADC:|ANCS:|"/g, '') : ''

const { t } = useI18n();

const {
    queryData,
    last90DayData,
    isQueryLoading,
    isLastDayLoading,
    handleViewMore,
} = useWhatsNew();
</script>

<style lang="scss">
.ai-card {
  background: radial-gradient(
    715.8% 143.12% at 97.01% -5.22%,
    #eef9f9 7.29%,
    #f1f8ff 92.32%
  );
}
</style>
